import { create } from 'zustand';

/**
 * Version type for file versions - matches the database schema
 * Used for tracking version processing status during polling
 *
 * Note: Exported for use by useVersionProgress hook to avoid duplicate type definitions
 */
export interface Version {
  id: string;
  workspace_id: string;
  table_id: string;
  operation_id?: string | null;
  file_name: string;
  file_size: number;
  status: 'queued' | 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  records_processed: number;
  records_total: number;
  error_message?: string | null;
  tags: string[] | Record<string, unknown> | null;
  created_by: string;
  created_at: string;
  completed_at?: string | null;
}

/**
 * Version store state and actions
 *
 * Minimal state management for version processing status polling.
 * Used by useVersionProgress hook to track processing status.
 *
 * Note: For uploading versions, use the useVersionUpload hook instead.
 * This store is only for tracking processing status after upload.
 */
interface VersionStore {
  // State
  currentVersion: Version | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentVersion: (version: Version | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Reset all state to initial values */
  reset: () => void;
}

export const useVersionStore = create<VersionStore>()((set) => ({
  // State
  currentVersion: null,
  isLoading: false,
  error: null,

  // Actions
  setCurrentVersion: (version) => set({ currentVersion: version }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  reset: () => set({ currentVersion: null, isLoading: false, error: null }),
}));
